import type { StateCreator } from "zustand";
import type { Raffle } from "@/campaigns/modules/raffle/types";
import type { RaffleProgressSchema } from "@/types";
import {
  campaignKey,
  matchesKey,
  type CampaignKey,
} from "@/campaigns/campaign-key";
import type { CampaignStreamState } from "./campaigns.store";
import type { AppStore } from "./app.store";

export interface RaffleSlice {
  /**
   * Assembled view objects — a two-way join (campaigns ⋈ raffle_progress), rebuilt on either
   * changing. One entry per raffle campaign.
   */
  raffles: Raffle[];
  setRaffles: (raffles: Raffle[]) => void;
  clearRaffles: () => void;

  /** Raw `raffle_progress` stream, keyed by campaign key (the per-player volatile tail). */
  raffleProgressById: Record<CampaignKey, RaffleProgressSchema>;
  raffleProgressStream: CampaignStreamState;
  setRaffleProgress: (rows: RaffleProgressSchema[]) => void;
  setRaffleProgressSubscribed: (subscribed: boolean) => void;
  setRaffleProgressLoading: (loading: boolean) => void;
  clearRaffleProgress: () => void;
}

const initialStream: CampaignStreamState = {
  subscribed: false,
  loading: false,
};

export const createRaffleSlice: StateCreator<RaffleSlice> = (set) => ({
  raffles: [],
  setRaffles: (raffles) => set({ raffles }),
  clearRaffles: () => set({ raffles: [] }),

  raffleProgressById: {},
  raffleProgressStream: initialStream,
  setRaffleProgress: (rows) =>
    set(() => {
      const byId: Record<CampaignKey, RaffleProgressSchema> = {};
      for (const row of rows) {
        byId[
          campaignKey({
            id: row.campaign_id,
            custom_id: row.campaign_custom_id,
          })
        ] = row;
      }
      return { raffleProgressById: byId };
    }),
  setRaffleProgressSubscribed: (subscribed) =>
    set((state) => ({
      raffleProgressStream: {
        ...state.raffleProgressStream,
        subscribed,
        loading: false,
      },
    })),
  setRaffleProgressLoading: (loading) =>
    set((state) => ({
      raffleProgressStream: { ...state.raffleProgressStream, loading },
    })),
  clearRaffleProgress: () =>
    set({ raffleProgressById: {}, raffleProgressStream: initialStream }),
});

/** Resolve a raffle campaign by its custom_id or numeric id (as string). */
export function selectRaffleById(
  state: AppStore,
  id: string
): Raffle | undefined {
  return state.raffles.find((r) => matchesKey(r.campaign, id));
}
